import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Calendar, User, Search, X } from 'lucide-react';
import Navbar from '../Navbar';
import Footer from '../Footer';
import { blogPosts, pageContent } from '../../utils/blogData';

const categories = {
  en: ['All', 'Sustainability', 'Compliance', 'Product Guide', 'Packaging', 'Industry Insights'],
  tr: ['Tumu', 'Surdurulebilirlik', 'Uyumluluk', 'Urun Rehberi', 'Ambalaj', 'Sektor Gorusleri']
};

const BlogPage = () => {
  const { language } = useLanguage();
  const posts = blogPosts[language];
  const t = pageContent[language];
  const cats = categories[language];

  const [activeCategory, setActiveCategory] = useState(cats[0]);
  const [searchQuery, setSearchQuery] = useState('');

  // Reset category filter when language changes
  useEffect(() => {
    setActiveCategory(cats[0]);
  }, [language, cats]);

  const filteredPosts = useMemo(() => {
    let result = posts;

    // Category filter
    if (activeCategory !== cats[0]) {
      result = result.filter(p => p.category === activeCategory);
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p =>
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q)
      );
    }

    return result;
  }, [posts, activeCategory, searchQuery, cats]);

  const featured = filteredPosts[0];
  const gridPosts = filteredPosts.slice(1);

  return (
    <div className="min-h-screen bg-white" data-testid="blog-page">
      <Navbar forceSolid />

      {/* Header */}
      <section className="pt-32 pb-8 md:pt-40 md:pb-10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1
            className="text-4xl md:text-5xl font-light text-gray-900 mb-6 italic"
            style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
            data-testid="blog-title"
          >
            {t.title}
          </h1>
          <div className="w-full max-w-2xl mx-auto border-t border-gray-300 mb-6" />
          <p className="text-base md:text-lg text-gray-500" data-testid="blog-subtitle">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Search + Category Filter */}
      <section className="pb-10">
        <div className="max-w-6xl mx-auto px-6">
          {/* Search */}
          <div className="relative max-w-md mx-auto mb-8" data-testid="blog-search">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={language === 'tr' ? 'Makalelerde ara...' : 'Search articles...'}
              className="w-full pl-11 pr-10 py-3 border border-gray-200 rounded-full text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-colors bg-gray-50"
              data-testid="blog-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                data-testid="blog-search-clear"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Categories */}
          <div className="flex flex-wrap justify-center gap-2" data-testid="blog-categories">
            {cats.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-1.5 text-sm rounded-full border transition-all duration-200 ${
                  activeCategory === cat
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400 hover:text-gray-700'
                }`}
                data-testid={`blog-category-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* No results */}
      {filteredPosts.length === 0 && (
        <section className="py-20 text-center">
          <p className="text-gray-400 text-lg" data-testid="blog-no-results">
            {language === 'tr' ? 'Sonuc bulunamadi.' : 'No results found.'}
          </p>
        </section>
      )}

      {/* Featured Post */}
      {featured && (
        <section className="pb-16">
          <div className="max-w-6xl mx-auto px-6">
            <Link
              to={`/blog/${featured.id}`}
              className="group block"
              data-testid="blog-featured-post"
            >
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="aspect-[16/10] rounded-2xl overflow-hidden">
                  <img
                    src={featured.image}
                    alt={featured.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div>
                  <span className="inline-block text-xs font-semibold uppercase tracking-wider text-teal-600 mb-3">
                    {featured.category}
                  </span>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 group-hover:text-gray-600 transition-colors">
                    {featured.title}
                  </h2>
                  <p className="text-gray-500 mb-6 leading-relaxed">
                    {featured.excerpt}
                  </p>
                  <div className="flex items-center text-sm text-gray-400 space-x-4">
                    <span className="flex items-center"><User size={14} className="mr-1.5" />{t.author}</span>
                    <span className="flex items-center"><Calendar size={14} className="mr-1.5" />{featured.date}</span>
                    <span>{featured.readTime}</span>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Divider */}
      {gridPosts.length > 0 && (
        <div className="max-w-6xl mx-auto px-6">
          <div className="border-t border-gray-200" />
        </div>
      )}

      {/* Post Grid */}
      {gridPosts.length > 0 && (
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
              {gridPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.id}`}
                  className="group"
                  data-testid={`blog-post-${post.id}`}
                >
                  <div className="aspect-[16/10] rounded-xl overflow-hidden mb-5">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <span className="inline-block text-xs font-semibold uppercase tracking-wider text-teal-600 mb-2">
                    {post.category}
                  </span>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-gray-600 transition-colors leading-snug">
                    {post.title}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4 line-clamp-2">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center text-xs text-gray-400 space-x-3">
                    <span className="flex items-center"><User size={12} className="mr-1" />{t.author}</span>
                    <span className="flex items-center"><Calendar size={12} className="mr-1" />{post.date}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BlogPage;
